/**
 * LA SAISIE D'UN TARIF — partagée par l'assistant (création) et la fiche
 * (ajout, modification). Modèle pur (22/10/2026, contrat `soirees-v1` §4.5,
 * §4.10, §5.2, §5.3).
 *
 * La saisie est du TEXTE (« 8,50 », « 02:30 ») ; le corps envoyé suit le
 * contrat (centimes, points de base, heures de Paris). Les contrôles ci-dessous
 * ne sont qu'un premier filtre, pour dire tout de suite ce que la billetterie
 * refuserait : elle reste seule juge, et son message s'affiche tel quel.
 *
 * Les heures de vente se renvoient TELLES QU'ELLES ONT ÉTÉ LUES quand personne
 * n'y a touché (`vente_fin_paris`, même sous sa forme « …+01:00 ») : c'est ce
 * qui garde l'heure d'hiver de la nuit du 25/10 (§3.4).
 */

import {
  CANAL_LIBELLE, GROUPE_RX, HEURE_RX, INSTANT_RX, PARIS_RX,
  bpDepuisSaisie, centsDepuisSaisie, entierDepuisSaisie, nomVide, saisieDepuisBp, saisieDepuisCents, saisonDeParis,
  type Canal, type Tarif,
} from './soirees-maison-model.js'

export interface SaisieTarif {
  nom: string
  description: string
  canal: Canal
  /** En euros : « 8 », « 8,50 ». */
  prix: string
  frais: string
  /** En % : « 5,5 » ; vide = le réglage de la billetterie (création seulement). */
  tva: string
  /** Vide = sans quota. */
  quota: string
  cascade_groupe: string
  cascade_rang: string
  /** Heure de Paris « AAAA-MM-JJTHH:MM », ou l'instant exact LU (« …+01:00 ») ; vide = aucune. */
  vente_debut: string
  vente_fin: string
  /** « HH:MM » de la nuit ; vide = aucune. */
  entree_avant: string
  /** Vide = toutes les salles de la soirée. */
  salles: string[]
  exoneration: boolean
  cache: boolean
  /** Vide = en fin de liste (création). */
  ordre: string
  consigne_porte: string
}

export type ChampTarif = keyof SaisieTarif

export function saisieVierge(): SaisieTarif {
  return {
    nom: '', description: '', canal: 'SUR_PLACE', prix: '', frais: '', tva: '', quota: '',
    cascade_groupe: '', cascade_rang: '', vente_debut: '', vente_fin: '', entree_avant: '',
    salles: [], exoneration: false, cache: false, ordre: '', consigne_porte: '',
  }
}

/** La saisie d'un tarif existant : chaque champ tel que lu, prêt à repartir inchangé. */
export function saisieDepuisTarif(t: Tarif): SaisieTarif {
  return {
    nom: t.nom, description: t.description ?? '', canal: t.canal,
    prix: saisieDepuisCents(t.prix_cents), frais: t.frais_service_cents ? saisieDepuisCents(t.frais_service_cents) : '',
    tva: saisieDepuisBp(t.tva_bp), quota: t.quota === null ? '' : String(t.quota),
    cascade_groupe: t.cascade_groupe ?? '', cascade_rang: t.cascade_rang === null ? '' : String(t.cascade_rang),
    vente_debut: t.vente_debut_paris ?? '', vente_fin: t.vente_fin_paris ?? '',
    entree_avant: t.entree_avant_heure ?? '',
    salles: t.salles ? [...t.salles] : [], exoneration: t.exoneration, cache: t.cache,
    ordre: String(t.ordre), consigne_porte: t.consigne_porte ?? '',
  }
}

/**
 * « Dupliquer » (23/10/2026) : la saisie d'un NOUVEAU tarif, copie de
 * celui-ci. Il quitte sa cascade (deux tarifs ne partagent pas un rang) et
 * prend la fin de la liste ; rien n'est enregistré avant la barre.
 */
export function saisieCopieDe(t: Tarif): SaisieTarif {
  return { ...saisieDepuisTarif(t), nom: `${t.nom} (copie)`.slice(0, 80), cascade_groupe: '', cascade_rang: '', ordre: '' }
}

export type DebutDeVente = 'mise' | 'date' | 'apres'

/** Le début de vente tel que la fenêtre le présente : dans une cascade, à une date, ou dès la mise en vente. */
export function debutDeVente(v: SaisieTarif): DebutDeVente {
  return v.cascade_groupe.trim() ? 'apres' : v.vente_debut ? 'date' : 'mise'
}

/**
 * Passer d'un début à l'autre : « dès la mise en vente » efface la date et la
 * cascade ; « à une date » quitte la cascade et garde la date ; « après un
 * autre tarif » garde la date (le tarif ne s'ouvrira pas avant).
 */
export function avecDebut(v: SaisieTarif, d: DebutDeVente): SaisieTarif {
  if (d === 'mise') return { ...v, vente_debut: '', cascade_groupe: '', cascade_rang: '' }
  if (d === 'date') return { ...v, cascade_groupe: '', cascade_rang: '' }
  return v
}

/** Les cascades qu'un tarif de ce canal peut rejoindre, avec le rang qui suit le dernier. */
export function cascadesARejoindre(tarifs: readonly Tarif[], canal: Canal, sauf: string | null): { groupe: string; rang: number; noms: string[] }[] {
  const groupes = new Map<string, Tarif[]>()
  for (const t of tarifs) {
    if (t.id === sauf || t.canal !== canal || !t.cascade_groupe) continue
    groupes.set(t.cascade_groupe, [...(groupes.get(t.cascade_groupe) ?? []), t])
  }
  return [...groupes.entries()].map(([groupe, ts]) => {
    const tries = ts.sort((a, b) => (a.cascade_rang ?? 0) - (b.cascade_rang ?? 0))
    return { groupe, rang: Math.max(...tries.map((x) => x.cascade_rang ?? 0)) + 1, noms: tries.map((x) => x.nom) }
  })
}

/** Ce que montre un champ « date et heure » : la partie locale, sans le décalage relu. */
export function valeurChampDateHeure(v: string): string {
  return v.slice(0, 16)
}

/**
 * « (hiver) » / « (été) » quand la valeur relue précise une heure ambiguë. Un
 * instant à la seconde porte toujours son décalage : il ne compte que dans
 * l'heure qui existe deux fois.
 */
export function saisonDeLaValeur(v: string): 'hiver' | 'été' | null {
  return saisonDeParis(v)
}

/**
 * Le champ « date et heure » a changé : on garde la valeur relue si l'heure
 * affichée est la même (rien n'a bougé), sinon la nouvelle heure de Paris.
 */
export function nouvelleDateHeure(avant: string, saisie: string): string {
  return saisie === valeurChampDateHeure(avant) ? avant : saisie
}

export type ErreursTarif = Partial<Record<ChampTarif, string>>

/** Le corps d'un tarif tel que le contrat le décrit (§4.5 ; tous les champs en modification, §4.10). */
export interface CorpsTarif {
  nom: string
  description: string | null
  canal: Canal
  prix_cents: number
  frais_service_cents: number
  tva_bp: number | null
  quota: number | null
  cascade_groupe: string | null
  cascade_rang: number | null
  vente_debut: string | null
  vente_fin: string | null
  entree_avant: string | null
  salles: string[] | null
  exoneration: boolean
  cache: boolean
  ordre?: number
  consigne_porte: string | null
}

const heureDeVenteValide = (v: string): boolean => PARIS_RX.test(v) || INSTANT_RX.test(v)

/**
 * La saisie → le corps du contrat, ou les erreurs champ par champ.
 * `mode: 'modification'` exige tous les champs (§4.10 : TVA non nulle, ordre).
 */
export function corpsTarif(s: SaisieTarif, mode: 'creation' | 'modification'): { corps: CorpsTarif } | { erreurs: ErreursTarif } {
  const e: ErreursTarif = {}
  const nom = s.nom.trim()
  if (!nom || nom.length > 80) e.nom = 'Le nom du tarif est obligatoire, 80 caractères au plus.'
  else if (nomVide(nom)) e.nom = 'Le nom du tarif ne peut pas être fait seulement d’espaces ou de caractères invisibles.'
  if (s.description.trim().length > 500) e.description = 'La description tient en 500 caractères.'
  const prix = centsDepuisSaisie(s.prix)
  if (prix === null) e.prix = 'Le prix est obligatoire (0 pour un tarif gratuit).'
  else if (prix === undefined || prix > 1_000_000) e.prix = 'Prix illisible : par exemple « 8 » ou « 8,50 », 10 000 € au plus.'
  const frais = centsDepuisSaisie(s.frais)
  if (frais === undefined || (frais ?? 0) > 100_000) e.frais = 'Frais illisibles : par exemple « 0,50 », 1 000 € au plus.'
  const tva = bpDepuisSaisie(s.tva)
  if (tva === undefined) e.tva = 'TVA illisible : par exemple « 5,5 » (de 0 à 100 %).'
  else if (tva === null && mode === 'modification') e.tva = 'La TVA est obligatoire.'
  const quota = entierDepuisSaisie(s.quota)
  if (quota === undefined || (quota !== null && (quota < 1 || quota > 20_000))) e.quota = 'Le quota va de 1 à 20 000 billets, ou reste vide (sans quota).'
  const groupe = s.cascade_groupe.trim()
  const rang = entierDepuisSaisie(s.cascade_rang)
  if (groupe && !GROUPE_RX.test(groupe)) e.cascade_groupe = 'Le groupe s’écrit en minuscules, chiffres et tirets (par exemple « sur-place »).'
  if (rang === undefined || (rang !== null && (rang < 1 || rang > 50))) e.cascade_rang = 'Le rang va de 1 à 50.'
  else if ((groupe === '') !== (rang === null)) e.cascade_rang = 'Une cascade demande un groupe ET un rang.'
  const debut = s.vente_debut.trim(); const fin = s.vente_fin.trim()
  if (debut && !heureDeVenteValide(debut)) e.vente_debut = 'Date et heure illisibles.'
  if (fin && !heureDeVenteValide(fin)) e.vente_fin = 'Date et heure illisibles.'
  if (debut && fin && !e.vente_debut && !e.vente_fin && fin.slice(0, 16) <= debut.slice(0, 16)) e.vente_fin = 'La fin de vente suit le début.'
  const entree = s.entree_avant.trim()
  if (entree && !HEURE_RX.test(entree)) e.entree_avant = 'Heure illisible : par exemple « 01:30 ».'
  const consigne = s.consigne_porte.trim()
  if (consigne.length > 80) e.consigne_porte = 'La consigne tient en 80 caractères.'
  const ordre = entierDepuisSaisie(s.ordre)
  if (ordre === undefined || (ordre !== null && ordre > 1000)) e.ordre = 'L’ordre va de 0 à 1 000.'
  else if (ordre === null && mode === 'modification') e.ordre = 'L’ordre est obligatoire.'
  if (s.salles.length > 16) e.salles = '16 salles au plus.'

  // Règles de saisie de la billetterie (§5.2), sur leur champ (§3.9).
  const p = typeof prix === 'number' ? prix : null
  const f = typeof frais === 'number' ? frais : 0
  if (s.exoneration && (s.canal !== 'SUR_PLACE' || (p ?? 0) > 0 || f > 0)) e.exoneration = 'Une exonération est gratuite, sans frais, et se fait sur place.'
  else if (s.canal === 'INVITATION' && (p ?? 0) > 0 && !e.prix) e.prix = 'Une invitation est gratuite.'
  else if (s.canal === 'SUR_PLACE' && p === 0 && !s.exoneration) e.exoneration = 'Un tarif sur place gratuit est forcément une exonération (motif demandé à la caisse).'
  if (s.canal === 'SUR_PLACE' && f > 0 && !e.frais) e.frais = 'Pas de frais de service sur place.'

  if (Object.keys(e).length > 0) return { erreurs: e }
  const corps: CorpsTarif = {
    nom, description: s.description.trim() || null, canal: s.canal,
    prix_cents: p ?? 0, frais_service_cents: f, tva_bp: tva ?? null, quota: quota ?? null,
    cascade_groupe: groupe || null, cascade_rang: rang ?? null,
    vente_debut: debut || null, vente_fin: fin || null, entree_avant: entree || null,
    salles: s.salles.length > 0 ? [...s.salles] : null,
    exoneration: s.exoneration, cache: s.cache, consigne_porte: consigne || null,
  }
  if (typeof ordre === 'number') corps.ordre = ordre
  return { corps }
}

/** Nombre de champs modifiés (pour la Save Bar). */
export function champsModifies(lu: SaisieTarif, s: SaisieTarif): number {
  let n = 0
  for (const k of Object.keys(lu) as ChampTarif[]) {
    const a = lu[k]; const b = s[k]
    if (Array.isArray(a) && Array.isArray(b) ? a.join() !== b.join() : a !== b) n++
  }
  return n
}

/** `champs` d'un refus de la billetterie (`prix_cents`, `tarifs.2.exoneration`) → le champ de l'écran. */
export function champDeLaSaisie(champ: string): ChampTarif | null {
  const brut = champ.replace(/^tarifs\.\d+\./, '')
  const correspondance: Record<string, ChampTarif> = {
    nom: 'nom', description: 'description', canal: 'canal', prix_cents: 'prix', frais_service_cents: 'frais',
    tva_bp: 'tva', quota: 'quota', cascade_groupe: 'cascade_groupe', cascade_rang: 'cascade_rang',
    vente_debut: 'vente_debut', vente_fin: 'vente_fin', entree_avant: 'entree_avant', salles: 'salles',
    exoneration: 'exoneration', cache: 'cache', ordre: 'ordre', consigne_porte: 'consigne_porte',
  }
  return correspondance[brut] ?? null
}

/**
 * Ce qui sera enregistré SANS EFFET aujourd'hui (§5.3), dit avant la création
 * avec les mots mêmes de la billetterie. `rangSuivant` : un autre tarif du même
 * groupe vient après celui-ci (la cascade peut rester bloquée).
 */
export function avertissementsSaisie(s: SaisieTarif, o: { rangSuivant?: boolean } = {}): string[] {
  const out: string[] = []
  if (s.canal === 'EN_LIGNE') out.push('Vente en ligne pas encore ouverte : ce tarif ne se vend nulle part pour l’instant.')
  if (s.canal === 'INVITATION') out.push('Aucun écran n’émet encore d’invitation.')
  if (s.cache) out.push('« Caché » est enregistré, mais rien ne le lit encore.')
  if (s.salles.length > 0) out.push('Les salles d’un tarif sont affichées à la porte, pas contrôlées.')
  if (s.canal === 'SUR_PLACE' && s.entree_avant.trim()) out.push('Un billet vendu à la porte est entré dès la vente : l’heure limite ne joue pas. Utilisez la fin de vente.')
  if (s.canal === 'SUR_PLACE' && s.consigne_porte.trim()) out.push('La consigne ne s’affiche qu’au scan d’un billet ; un billet vendu à la caisse entre sans scan.')
  if (s.canal === 'SUR_PLACE' && !s.vente_debut.trim()) out.push('Sans début de vente, ce tarif se vend dès la mise en vente, même des jours avant, à la caisse de la page de secours ; chaque vente y compte aussitôt comme une entrée.')
  if (o.rangSuivant && s.cascade_groupe.trim() && !s.quota.trim() && !s.vente_fin.trim()) {
    out.push('Sans quota ni fin de vente, ce tarif ne se libère jamais : les suivants de la cascade ne s’ouvriront pas.')
  }
  return out
}

/** « Sur place · 8,00 € · cascade sur-place, rang 1 » — une ligne de résumé. */
export function resumeSaisie(s: SaisieTarif): string {
  const prix = centsDepuisSaisie(s.prix)
  const morceaux = [CANAL_LIBELLE[s.canal], typeof prix === 'number' ? (prix === 0 ? 'gratuit' : `${saisieDepuisCents(prix)} €`) : 'prix à saisir']
  if (s.quota.trim()) morceaux.push(`${s.quota.trim()} billets au plus`)
  if (s.cascade_groupe.trim() && s.cascade_rang.trim()) morceaux.push(`cascade ${s.cascade_groupe.trim()}, rang ${s.cascade_rang.trim()}`)
  if (s.exoneration) morceaux.push('exonération')
  return morceaux.join(' · ')
}
